import { getPresignedURLForViewer } from './interfaceS3';
import { createShortShareUrl } from './urlShortener';

/**
 * Generates a shareable link for a classic service dataset
 * @param {string} userSub - User's sub from authentication
 * @param {string} idToken - JWT token
 * @param {string} region - AWS region
 * @param {string} idpool - Identity pool ID
 * @param {string} filename - File name
 * @param {string} description - File description
 * @param {number} expirationHours - Hours until expiration (default: 168 = 7 days)
 * @param {boolean} useShortUrl - Whether to create a shortened URL (default: true)
 * @param {string} strapiJwt - Strapi JWT token for URL shortening
 * @returns {Promise<string>} - The shareable URL (shortened or full)
 */
export async function generateShareLink(userSub, idToken, region, idpool, filename, description, expirationHours = 168, useShortUrl = true, strapiJwt = null) {
  try {
    // Generate presigned URLs with long expiration (7 days by default)
    const expirationSeconds = expirationHours * 3600;
    const { metadata, hierarchy, octree } = await getPresignedURLForViewer(
      userSub, 
      idToken, 
      region, 
      idpool, 
      filename, 
      expirationSeconds
    );

    // Create the data object to encode
    const shareData = {
      filename,
      description,
      metadata,
      hierarchy,
      octree,
      expiresAt: Date.now() + (expirationHours * 60 * 60 * 1000) // Add expiration timestamp
    };

    // Encode the data as base64
    const encodedData = btoa(JSON.stringify(shareData));
    
    // Create the shareable URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/public/potree/${encodeURIComponent(encodedData)}`;
    
    // If URL shortening is enabled, create a short URL
    if (useShortUrl && strapiJwt) {
      try {
        console.log('Attempting to create short URL for:', shareUrl);
        const shortUrl = await createShortShareUrl(shareUrl, strapiJwt, expirationHours);
        console.log('Short URL created successfully:', shortUrl);
        return shortUrl;
      } catch (error) {
        console.warn('Failed to create short URL, falling back to full URL:', error);
        return shareUrl;
      }
    } else {
      console.log('Skipping URL shortening:', { useShortUrl, hasJwt: !!strapiJwt });
    }
    
    return shareUrl;
  } catch (error) {
    console.error('Error generating share link:', error);
    throw new Error('Failed to generate share link');
  }
}

/**
 * Decodes a share link to extract the data
 * @param {string} shareId - The encoded share ID from the URL
 * @returns {Object} - The decoded share data
 */
export function decodeShareLink(shareId) {
  try {
    const decodedData = JSON.parse(atob(decodeURIComponent(shareId)));
    
    // Check if the link has expired
    if (decodedData.expiresAt && Date.now() > decodedData.expiresAt) {
      throw new Error('Share link has expired');
    }
    
    return decodedData;
  } catch (error) {
    console.error('Error decoding share link:', error);
    throw new Error('Invalid or expired share link');
  }
}